import React, { FunctionComponent, useRef, useState } from 'react';
import { Form, Input, Button, Card, Spin } from 'antd';
import authStore, { checkTotp, getToken, setupTotp } from '@stores/auth';
import { useStore } from 'effector-react';
import { Redirect } from 'react-router';
import routes from '@config/routes';
import css from './Login.module.less';
import useTranslate from '@hooks/useTranslate';
import componentContent from './Login.content.json';
import { INPUT_FIELD_MAX_LENGTH } from '../../constants/limits';
import { QRCodeSVG } from 'qrcode.react';

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const tailLayout = {
  wrapperCol: { offset: 8, span: 16 },
};

interface LoginProps {}

const Login: FunctionComponent<LoginProps> = () => {
  const [isTotpStep, setIsTotpStep] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [loginCreds, setLoginCreds] = useState<Record<string, string> | null>(null);
  const [form] = Form.useForm();
  const [totpValue, setTotpValue] = useState('');
  const [totpError, setTotpError] = useState('');
  const totpInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const auth = useStore(authStore);
  const content = useTranslate(componentContent);
  const isAuth = !!auth.user;

  const isLoading =
    !!auth.preloaders.getToken || !!auth.preloaders.checkTotp || !!auth.preloaders.setupTotp;

  const handleChangeTotp = (value: string, index: number) => {
    const digit = value.replace(/\D/g, '').slice(-1);

    const nextValue = totpValue.split('');
    nextValue[index] = digit;

    const result = nextValue.join('').slice(0, 6);

    setTotpValue(result);
    form.setFieldsValue({ totp: result });

    if (result.length === 6) {
      setTotpError('');
    }

    if (digit && index < 5) {
      totpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDownTotp = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace' && !totpValue[index] && index > 0) {
      totpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleBackToCredentials = () => {
    setIsTotpStep(false);
    setQrCode(null);
    setLoginCreds(null);
    setTotpValue('');
    setTotpError('');
    form.setFieldsValue({ totp: undefined });
  };

  const onFinish = async (values: Record<string, any>) => {
    if (!isTotpStep) {
      const creds = {
        username: values.username,
        password: values.password,
      };

      const checkResponse = await checkTotp(creds);

      setLoginCreds(creds);

      if (checkResponse.setupNeeded) {
        const setupResponse = await setupTotp(creds);
        setQrCode(setupResponse.qrCode);
      } else {
        setQrCode(null);
      }

      setIsTotpStep(true);
      return;
    }

    if (!loginCreds) return;

    if (totpValue.length !== 6) {
      setTotpError(content.totp_message);
      return;
    }

    getToken({
      username: loginCreds.username,
      password: loginCreds.password,
      totp: totpValue,
    });
  };

  if (isAuth) return <Redirect to={routes.root.path} />;

  const loginComponent = (
    <>
      <Form.Item
        label={content.username_label}
        name='username'
        rules={[
          {
            required: true,
            message: content.username_message,
          },
        ]}
      >
        <Input maxLength={INPUT_FIELD_MAX_LENGTH} />
      </Form.Item>

      <Form.Item
        label={content.password_label}
        name='password'
        rules={[{ required: true, message: content.password_message }]}
      >
        <Input.Password maxLength={INPUT_FIELD_MAX_LENGTH} />
      </Form.Item>
    </>
  );

  const totpComponent = (
    <>
      <Form.Item className={css.backButtonFormItem}>
        <Button
          type='link'
          onClick={handleBackToCredentials}
          disabled={isLoading}
          className={css.backButton}
        >
          ← {content.back_button_text}
        </Button>
      </Form.Item>

      {qrCode && (
        <Form.Item className={css.qrFormItem}>
          <div className={css.qrBlock}>
            <div className={css.qrTitle}>{content.qr_url_title}</div>

            <QRCodeSVG
              value={qrCode}
              size={220}
            />

            <div className={css.qrHint}>{content.qr_url_hint}</div>
          </div>
        </Form.Item>
      )}

      <Form.Item
        label={content.totp_label}
        required
        validateStatus={totpError ? 'error' : undefined}
        help={totpError}
      >
        <div className={css.totpCells}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Input
              key={index}
              ref={(element) => {
                totpInputRefs.current[index] = element?.input || null;
              }}
              className={css.totpCell}
              value={totpValue[index] || ''}
              maxLength={1}
              inputMode='numeric'
              onChange={(event) => handleChangeTotp(event.target.value, index)}
              onKeyDown={(event) => handleKeyDownTotp(event, index)}
            />
          ))}
        </div>
      </Form.Item>
    </>
  );

  return (
    <div className={css.root}>
      <Spin spinning={isLoading}>
        <Card>
          <Form
            {...layout}
            form={form}
            name='auth'
            initialValues={{ remember: true }}
            onFinish={onFinish}
          >
            {!isTotpStep ? loginComponent : totpComponent}

            <Form.Item
              {...tailLayout}
              style={{ marginBottom: 0 }}
            >
              <Button
                type='primary'
                htmlType='submit'
                disabled={isLoading}
              >
                {isTotpStep ? content.login_button_text : content.continue_button_text}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default Login;
